import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Menu, ChevronRight } from 'lucide-react';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  description: string;
}

interface DashboardSidebarProps {
  title: string;
  menuItems: MenuItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  children?: React.ReactNode;
}

export function DashboardSidebar({ 
  title, 
  menuItems, 
  activeTab, 
  onTabChange, 
  children 
}: DashboardSidebarProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMenuItemClick = (tabId: string) => {
    onTabChange(tabId);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  return (
    <>
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className={`
        ${isMobile ? 'fixed' : 'relative'} 
        ${sidebarOpen ? 'w-64' : 'w-16'} 
        ${isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'}
        transition-all duration-300 ease-in-out bg-white border-r border-gray-200 flex flex-col z-50
        ${isMobile ? 'h-full' : 'min-h-screen'}
        shadow-lg
      `}>
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className={`overflow-hidden transition-all duration-300 ${sidebarOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'}`}>
              <h2 className="text-lg font-semibold text-black whitespace-nowrap">{title}</h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors duration-200"
            >
              {sidebarOpen ? (
                <ChevronLeft className="h-4 w-4 text-gray-600" />
              ) : (
                <Menu className="h-4 w-4 text-gray-600" />
              )}
            </Button>
          </div>
        </div>

        <nav className="flex-1 p-3">
          <div className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleMenuItemClick(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all duration-200 ${
                  activeTab === item.id
                    ? 'bg-gray-100 text-black font-medium shadow-sm border border-gray-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-black'
                }`}
                title={!sidebarOpen ? item.label : ''}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <div className={`flex-1 min-w-0 overflow-hidden transition-all duration-300 ${
                  sidebarOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'
                }`}>
                  <p className="font-medium whitespace-nowrap">{item.label}</p>
                  <p className="text-xs text-gray-500 truncate whitespace-nowrap">{item.description}</p>
                </div>
                {sidebarOpen && activeTab === item.id && (
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                )}
              </button>
            ))}
          </div>
        </nav>

        {children && (
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            {children}
          </div>
        )}
      </div>

      {isMobile && !sidebarOpen && (
        <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 p-4 z-30 shadow-sm">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(true)}
              className="hover:bg-gray-100"
            >
              <Menu className="h-4 w-4" />
            </Button>
            <h1 className="text-lg font-semibold text-black">{title}</h1>
            <div className="w-8" />
          </div>
        </div>
      )}
    </>
  );
}
